import type { ComponentType, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  NOTIFICATION_DEFAULT_DISPLAY_DURATION,
  NOTIFICATION_INFINITE_DISPLAY_DURATION,
} from '../../application/NotificationService';
import type { NotificationType } from '../../application/NotificationService';
import { useNotificationService } from './NotificationServiceContext';

type Notifications = ReadonlyArray<{
  node: ReactNode;
  id: string;
}>;

type NotificationContainerComponentType = ComponentType<{
  notifications: Notifications;
}>;

type NotificationComponentType = ComponentType<
  React.PropsWithChildren<{
    type: NonNullable<NotificationType['type']>;
    onClick?: () => void;
  }>
>;

type CustomComponentType = ComponentType<
  React.PropsWithChildren<{
    hide: () => void;
  }>
>;

export const DisplayNotificationSubscriber = ({
  NotificationContainerComponent,
  DefaultNotificationComponent,
}: {
  NotificationContainerComponent: NotificationContainerComponentType;
  DefaultNotificationComponent: NotificationComponentType;
}) => {
  const notificationService = useNotificationService();
  const [notifications, setNotifications] = useState<Notifications>([]);

  const hideNotification = (idToHide: string) => {
    setNotifications(previousNotifications =>
      previousNotifications.filter(({ id }) => id !== idToHide),
    );
  };

  const handleNewNotification = useCallback(
    ({
      id,
      message,
      component,
      durationInMs,
      type,
    }: NotificationType & {
      id: string;
      component?: CustomComponentType;
    }) => {
      setNotifications(previousNotifications => [
        ...previousNotifications,
        {
          node: (
            <NotificationNode
              DefaultNotificationComponent={DefaultNotificationComponent}
              component={component}
              message={message}
              type={type}
              durationInMs={durationInMs}
              id={id}
              hideNotification={hideNotification}
            />
          ),
          id,
        },
      ]);
    },
    [DefaultNotificationComponent],
  );

  useEffect(() => {
    const subscriberId = notificationService.subscribe({
      handleNewNotification,
    });

    return () => {
      notificationService.unsubscribe(subscriberId);
    };
  }, [handleNewNotification, notificationService]);
  return <NotificationContainerComponent notifications={notifications} />;
};

export const hideNotificationAfterDuration = ({
  durationInMs,
  hideFunction,
}: {
  durationInMs: number;
  hideFunction: () => void;
}) =>
  durationInMs !== NOTIFICATION_INFINITE_DISPLAY_DURATION
    ? setTimeout(hideFunction, durationInMs)
    : null;

const NotificationNode = ({
  DefaultNotificationComponent,
  component: CustomComponent,
  type = 'info',
  message,
  hideNotification,
  id,
  durationInMs = NOTIFICATION_DEFAULT_DISPLAY_DURATION,
}: {
  DefaultNotificationComponent: NotificationComponentType;
  component?: CustomComponentType;
  type: NotificationType['type'];
  message: React.ReactNode;
  hideNotification: (id: string) => void;
  durationInMs?: number;
  id: string;
}) => {
  const autoHideTimeoutId = hideNotificationAfterDuration({
    durationInMs,
    hideFunction: () => hideNotification(id),
  });

  const hideFunction = () => {
    if (autoHideTimeoutId) {
      clearTimeout(autoHideTimeoutId);
    }
    return hideNotification(id);
  };

  if (CustomComponent) {
    return <CustomComponent hide={hideFunction}>{message}</CustomComponent>;
  }
  return (
    <DefaultNotificationComponent type={type} onClick={hideFunction}>
      {message}
    </DefaultNotificationComponent>
  );
};
